import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input as routerInput } from '@angular/core';
import { Router, ActivatedRoute, RouterLink, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';
import {
  ConfirmModalComponent,
  DetailsPageHeaderComponent,
  DetailsPageHeaderPlaceholderComponent,
  DetailsPageContentPlaceholderComponent,
  TransactionLimitsComponent,
  UpdateTransactionLimitsComponent,
} from '@shared/components';
import { Breadcrumb, FinancialAccountDetails, LimitItem } from '@shared/models';
import { LimitAccountType, toTitleCase } from '@shared/utils';
import { FinancialAcccountActions, financialAccountFeature } from '@store';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { FinancialAccountBalancesComponent } from '../financial-account-balances/financial-account-balances.component';
import { EditFinancialAccountComponent } from '@shared/components/financial-account/edit-financial-account/edit-financial-account.component';

@Component({
  selector: 'app-financial-account-page',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RktIconComponent,
    DetailsPageHeaderPlaceholderComponent,
    DetailsPageContentPlaceholderComponent,
    DetailsPageHeaderComponent,
    RouterLink,
    RouterOutlet,
    AsyncPipe,
    FinancialAccountBalancesComponent,
    TransactionLimitsComponent,
  ],
  templateUrl: './financial-account-page.component.html',
})
export class FinancialAccountPageComponent {
  modalService = inject(ModalService);
  store = inject(Store);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);

  businessAccountId = routerInput<string>();
  financialAccountId = routerInput.required<string>();

  tabBaseUrl = computed(() => {
    if (this.businessAccountId()) {
      return `/app/business-accounts/${this.businessAccountId()}/financial-accounts/${this.financialAccountId()}`;
    }
    return `/app/financial-accounts/${this.financialAccountId()}`;
  });

  tabItems = computed(() => {
    const base = [
      { tabName: 'Account Information', href: this.tabBaseUrl() },
      { tabName: 'Account Activity', href: `${this.tabBaseUrl()}/activities` },
      { tabName: 'Journal Activity', href: `${this.tabBaseUrl()}/journals` },
      { tabName: 'Child Accounts', href: `${this.tabBaseUrl()}/child-accounts` },
    ];

    const category = this.financialAccountCategory();
    if (category === 'INTEGRATED' || category === 'INTERNAL') {
      base.push({ tabName: 'Funds on Hold', href: `${this.tabBaseUrl()}/funds-on-hold` });
    }

    return base;
  });

  pageReturnURL = computed(() => {
    if (this.businessAccountId()) {
      return `/app/business-accounts/${this.businessAccountId()}/financial-accounts`;
    }
    return '/app/financial-accounts';
  });

  breadcrumbs = computed((): Breadcrumb[] => {
    if (this.businessAccountId()) {
      return [
        { label: 'Business Accounts', url: '/app/business-accounts' },
        { label: 'Business Account Details', url: `/app/business-accounts/${this.businessAccountId()}` },
        { label: 'Financial Accounts', url: this.pageReturnURL() },
        { label: 'Financial Account Details' },
      ];
    }
    return [{ label: 'Financial Accounts', url: this.pageReturnURL() }, { label: 'Financial Account Details' }];
  });

  financialAccount$ = this.store.select(financialAccountFeature.selectFinancialAccountDetails);

  financialAccountCategory = toSignal(
    this.store.select(financialAccountFeature.selectFinancialAccountDetails).pipe(map((fa) => fa?.category)),
  );

  loadFinancialAccount$ = this.activatedRoute.params.pipe(
    map(() => this.store.dispatch(FinancialAcccountActions.loadFinancialAccountDetails())),
  );

  transactionLimits$ = this.store.select(financialAccountFeature.selectFinancialAccountLimits);

  onActionBtnClick(state: string): void {
    const action = state === 'SUSPENDED' ? 'unsuspend' : 'suspend';

    const serviceAccountRef = this.modalService.open(ConfirmModalComponent, {
      className: 'confirm-modal',
    });

    serviceAccountRef.componentInstance.confirmType = 'warning';
    serviceAccountRef.componentInstance.title = `${toTitleCase(action)} Financial Account`;
    serviceAccountRef.componentInstance.text = `Are you sure you want to ${action} this financial account?`;

    serviceAccountRef.result.then(
      (result) => {
        if (result) {
          this.store.dispatch(FinancialAcccountActions.updateFinancialAccountStatus({ action }));
        }
      },
      () => false,
    );
  }

  isActive(tabHref: string): boolean {
    return this.router.isActive(tabHref, {
      paths: 'exact',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  onEditTransactionLimitsClick(financialAccountId: string, transactionLimits: LimitItem[] | null, currency: string): void {
    if (!transactionLimits) {
      return;
    }

    const limitsModalRef = this.modalService.open(UpdateTransactionLimitsComponent, {
      className: 'entity-form-modal transaction-limits-edit-modal',
    });

    limitsModalRef.componentInstance.modalInitData({
      limitAccountId: financialAccountId,
      limitAccountType: LimitAccountType.FINANCIAL_ACCOUNT,
      transactionLimits,
      currency,
    });

    limitsModalRef.result.finally(() => this.store.dispatch(FinancialAcccountActions.loadFinancialAccountLimits()));
  }

  onEditBtnClick(financialAccount: FinancialAccountDetails) {
    const accountModalRef = this.modalService.open(EditFinancialAccountComponent, {
      className: 'entity-form-modal',
    });

    accountModalRef.componentInstance.modalInitData(financialAccount);

    accountModalRef.result.then(
      (financialAccount) => {
        if (financialAccount) this.store.dispatch(FinancialAcccountActions.loadFinancialAccountDetails());
      },
      () => false,
    );
  }
}
